/* eslint-disable react/no-danger */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ArtikelDetail, useArtikelDetailList } from 'api/artikelDetail';
import {
    artikelGruppenByNrSelector,
    einheitenByCodeSelector,
    lagerByIntNrSelector,
    lookupsLoadingFinishedSelector,
} from 'store/lookups/selector';
import { createRecordUrl, formatDecimal } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import classnames from 'classnames';
import { getArtikelDetailId } from 'utils/helper';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { authSelector } from 'store/auth/selector';
import useArtikelCalculator from 'hooks/useArtikelCalculator';
import { defaultArtikel } from 'api/artikel';
import * as XLSX from 'xlsx';
import moment from 'moment-timezone';

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 5,
    },
    tdBody: {
        paddingLeft: 5,
    },
    tableRow: {
        selectors: {
            '&:hover': {},
            '&:nth-child(odd)': {
                backgroundColor: palette.neutralLight,
            },
            '&:nth-child(even)': {
                backgroundColor: palette.neutralLighterAlt,
            },
        },
    },
});

export interface LagerstandRef {
    downloadXlsx: () => void;
}

interface LagerstandProps {
    artikelGruppeNrList?: number[];
    lagerIntNrList?: number[];
    minBestandVon: number;
    bestandBelowMinBestand: boolean;
    verfuegbarBelowMinBestand: boolean;
    bestandVon: number;
    offenBestVon: number;
    offenBestVonD: number;
    offenLiefVon: number;
    offenLiefVonD: number;
    offenResVon: number;
    includeArchiv: boolean;
    showOffenBest: boolean;
    showOffenLief: boolean;
    showLagerwert: boolean;
    printView?: boolean;
}

export const Lagerstand = React.forwardRef<LagerstandRef, LagerstandProps>(
    (
        {
            artikelGruppeNrList,
            lagerIntNrList,
            includeArchiv,
            minBestandVon,
            bestandBelowMinBestand,
            verfuegbarBelowMinBestand,
            bestandVon,
            offenBestVon,
            offenBestVonD,
            offenLiefVon,
            offenLiefVonD,
            offenResVon,
            showOffenBest,
            showOffenLief,
            showLagerwert,
            printView = false,
        },
        ref,
    ) => {
        const auth = useSelector(authSelector);
        const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
        const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
        const lagerByIntNr = useSelector(lagerByIntNrSelector);
        const einheitenByCode = useSelector(einheitenByCodeSelector);
        const artikelCalculator = useArtikelCalculator();

        const artikelDetailList = useArtikelDetailList({
            artikelGruppeNrList,
            lagerIntNrList,
            minBestandVon,
            bestandBelowMinBestand,
            verfuegbarBelowMinBestand,
            bestandVon,
            offenBestVon,
            offenBestVonD,
            offenLiefVon,
            offenLiefVonD,
            offenResVon,
            includeArchiv,
            withDefaultKalkulation: showLagerwert,
            withDrehung: true,
        });

        const distinctArtikelGruppenNrList = React.useMemo<number[]>(() => {
            const nrUsed: { [key: number]: boolean } = {};

            (artikelDetailList.data || []).forEach((artikelDetail) => {
                nrUsed[artikelDetail.artikelGruppeNr] = true;
            });

            return Object.keys(nrUsed)
                .sort((a, b) => (artikelGruppenByNr[a]?.text || '').localeCompare(artikelGruppenByNr[b]?.text || ''))
                .map((nr) => parseInt(nr, 10));
        }, [artikelGruppenByNr, artikelDetailList.data]);

        const artikelDetailListByArtikelGruppeNr = React.useMemo<{ [key: string]: ArtikelDetail[] }>(() => {
            const newArtikelDetailListByArtikelGruppeNr: { [key: string]: ArtikelDetail[] } = {};

            distinctArtikelGruppenNrList.forEach((artikelGruppenNr2) => {
                newArtikelDetailListByArtikelGruppeNr[artikelGruppenNr2] = (artikelDetailList.data || []).filter(
                    (artikelDetail2) => artikelDetail2.artikelGruppeNr === artikelGruppenNr2,
                );
            });

            return newArtikelDetailListByArtikelGruppeNr;
        }, [distinctArtikelGruppenNrList, artikelDetailList.data]);

        const showAll = auth.rolle === 'ID' || auth.rolle === 'GF';

        const getHeaderData = (): Array<string> => {
            const headerData: Array<string> = [];

            headerData.push('Nr-Lager');
            headerData.push('Lager');
            headerData.push('Artikelgruppe');
            headerData.push('Artikel-Nr.');
            headerData.push('Artikel-Bezeichnung');
            headerData.push('Einh.');
            headerData.push('Regal');
            headerData.push('Fach');
            headerData.push('Bestand');
            headerData.push('Min-Best.');

            if (showAll) {
                headerData.push('Drehung');
            }

            headerData.push('Verfügbar');

            if (showOffenBest) {
                headerData.push('Off. Best.');
                headerData.push('Off. Best. D');
            }

            if (showOffenLief) {
                headerData.push('Off. Lief.');
                headerData.push('Off. Lief. D');
            }

            headerData.push('Reserviert');

            if (showAll && showLagerwert) {
                headerData.push('EK1');
                headerData.push('EK2');
                headerData.push('L-Wert');
                headerData.push('L-W. fiktiv');
            }

            return headerData;
        };

        React.useImperativeHandle(ref, () => ({
            downloadXlsx() {
                const data: Array<Array<string | number>> = [getHeaderData()];

                distinctArtikelGruppenNrList.forEach((artikelGruppenNr2) => {
                    (artikelDetailListByArtikelGruppeNr[artikelGruppenNr2] || []).forEach((artikelDetail) => {
                        const { value: artikelEhEk1 } = artikelCalculator.convertPreisByEinheit(
                            artikelDetail.artikel || defaultArtikel,
                            artikelDetail.defaultKalkulation?.ek1 || 0,
                            artikelDetail.defaultKalkulation?.ek1Eh,
                            artikelDetail.artikel?.einheit,
                        );

                        const { value: ek1ProEh } = artikelCalculator.convertPreisByEinheit(
                            artikelDetail.artikel || defaultArtikel,
                            artikelEhEk1,
                            artikelDetail.artikel?.einheit,
                            artikelDetail.defaultKalkulation?.eh,
                        );

                        const line: Array<string | number> = [];

                        line.push(artikelDetail.lagerArtikelNr);
                        line.push(
                            artikelDetail.lagerIntNr
                                ? lagerByIntNr[artikelDetail.lagerIntNr]?.name || artikelDetail.lagerIntNr
                                : '-',
                        );

                        line.push(artikelGruppenByNr[artikelGruppenNr2]?.text || '?');

                        line.push(artikelDetail.artikelNr);
                        line.push(artikelDetail.artikel?.text || '?');
                        line.push(
                            artikelDetail.artikel?.einheit
                                ? einheitenByCode[artikelDetail.artikel.einheit]?.text || artikelDetail.artikel.einheit
                                : '?',
                        );

                        line.push(artikelDetail.regal);
                        line.push(artikelDetail.fach);

                        line.push(artikelDetail.bestand);
                        line.push(artikelDetail.minBestand);
                        if (showAll) {
                            line.push(artikelDetail.drehung);
                        }
                        line.push(artikelDetail.bestand - artikelDetail.offenRes);
                        if (showOffenBest) {
                            line.push(artikelDetail.offenBest);
                            line.push(artikelDetail.offenBestD);
                        }
                        if (showOffenLief) {
                            line.push(artikelDetail.offenLief);
                            line.push(artikelDetail.offenLiefD);
                        }
                        line.push(artikelDetail.offenRes);
                        if (showAll && showLagerwert) {
                            line.push(artikelDetail.defaultKalkulation ? ek1ProEh : '?');
                            line.push(artikelDetail.defaultKalkulation ? artikelDetail.defaultKalkulation.ek2 : '?');
                            line.push(artikelDetail.defaultKalkulation ? ek1ProEh * artikelDetail.bestand : '?');
                            line.push(
                                artikelDetail.defaultKalkulation
                                    ? ek1ProEh * (artikelDetail.bestand + artikelDetail.offenBest)
                                    : '?',
                            );
                        }

                        data.push(line);
                    });
                });

                const workbook = XLSX.utils.book_new();
                const worksheet = XLSX.utils.aoa_to_sheet(data);

                XLSX.utils.book_append_sheet(workbook, worksheet, 'Lagerstand');

                const content = XLSX.write(workbook, {
                    bookType: 'xlsx',
                    type: 'buffer',
                    compression: true,
                    bookSST: true,
                });

                const file = new Blob([content], {
                    type: 'application/octet-stream',
                });

                const element = document.createElement('a');
                element.href = URL.createObjectURL(file);
                element.download = 'Lagerstand.xlsx';
                document.body.appendChild(element);
                element.click();
            },
        }));

        if (artikelDetailList.isLoading) {
            return <LoadingIndicator />;
        }

        const renderHeaderRow = () => (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportLagerArtikelNrPrintCell : formStyles.reportLagerArtikelNrCell,
                    )}
                >
                    Nr-Lager
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportLagerPrintCell : formStyles.reportLagerCell,
                    )}
                >
                    Lager
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportArtikelTextPrintCell : formStyles.reportArtikelTextCell,
                    )}
                >
                    Artikel
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportEinheitPrintCell : formStyles.reportEinheitCell,
                    )}
                >
                    Einh.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                    )}
                >
                    Bestand
                </td>
                {showAll && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Min-Best.
                    </td>
                )}
                {showAll && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Drehung
                    </td>
                )}
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                    )}
                >
                    Verfügbar
                </td>
                {showOffenBest && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Off. Best.
                    </td>
                )}
                {showOffenBest && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Off. Best. D
                    </td>
                )}
                {showOffenLief && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Off. Lief.
                    </td>
                )}
                {showOffenLief && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        Off. Lief. D
                    </td>
                )}
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                    )}
                >
                    Reserviert
                </td>
                {showAll && showLagerwert && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        EK1
                    </td>
                )}
                {showAll && showLagerwert && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        EK2
                    </td>
                )}
                {showAll && showLagerwert && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        L-Wert
                    </td>
                )}
                {showAll && showLagerwert && (
                    <td
                        className={classnames(
                            styles.tdHead,
                            formStyles.dataValueRight,
                            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
                        )}
                    >
                        L-W. fiktiv
                    </td>
                )}
            </tr>
        );

        let countTotal = 0;
        let lWertTotal = 0;
        let lWertVerfuegbarTotal = 0;
        let lWertOffenBestTotal = 0;
        let lWertOffenBestDTotal = 0;
        let lWertOffenLiefTotal = 0;
        let lWertOffenLiefDTotal = 0;
        let lWertReserviertTotal = 0;
        let lWertFiktivTotal = 0;

        distinctArtikelGruppenNrList.forEach((artikelGruppenNr2) => {
            (artikelDetailListByArtikelGruppeNr[artikelGruppenNr2] || []).forEach((artikelDetail) => {
                countTotal += 1;

                if (artikelDetail.defaultKalkulation) {
                    const { value: artikelEhEk1 } = artikelCalculator.convertPreisByEinheit(
                        artikelDetail.artikel || defaultArtikel,
                        artikelDetail.defaultKalkulation?.ek1 || 0,
                        artikelDetail.defaultKalkulation?.ek1Eh,
                        artikelDetail.artikel?.einheit,
                    );

                    const { value: ek1ProEh } = artikelCalculator.convertPreisByEinheit(
                        artikelDetail.artikel || defaultArtikel,
                        artikelEhEk1,
                        artikelDetail.artikel?.einheit,
                        artikelDetail.defaultKalkulation?.eh,
                    );

                    lWertTotal += ek1ProEh * artikelDetail.bestand;
                    lWertFiktivTotal += ek1ProEh * (artikelDetail.bestand + artikelDetail.offenBest);
                    lWertVerfuegbarTotal += ek1ProEh * (artikelDetail.bestand - artikelDetail.offenRes);
                    lWertOffenBestTotal += ek1ProEh * artikelDetail.offenBest;
                    lWertOffenBestDTotal += ek1ProEh * artikelDetail.offenBestD;
                    lWertOffenLiefTotal += ek1ProEh * artikelDetail.offenLief;
                    lWertOffenLiefDTotal += ek1ProEh * artikelDetail.offenLiefD;
                    lWertReserviertTotal += ek1ProEh * artikelDetail.offenRes;
                }
            });
        });

        if (!lookupsLoadingFinished) {
            return <div />;
        }

        return (
            <div className={printView ? styles.printContainer : styles.container}>
                {printView && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html:
                                // eslint-disable-next-line max-len
                                '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                        }}
                    />
                )}
                {!printView && <table className={formStyles.reportFixedHeader}>{renderHeaderRow()}</table>}
                <h1>Lagerstandliste</h1>
                {printView && (
                    <table className={formStyles.reportFilterInfo}>
                        <tr>
                            <td>Druckdatum: </td>
                            <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                        </tr>
                    </table>
                )}
                {showAll && (
                    <table className={formStyles.reportSummaryTable}>
                        <tr>
                            <td>Artikel - Gesamt:</td>
                            <td className={formStyles.dataValueRight}>{countTotal}</td>
                        </tr>
                        <tr>
                            <td>Lagerwert - Gesamt - Bestand:</td>
                            <td className={formStyles.dataValueRight}>{formatDecimal(lWertTotal, 2)}</td>
                        </tr>
                        {showOffenBest && (
                            <tr>
                                <td>Lagerwert - Gesamt - Bestand + Offene Bestellungen:</td>
                                <td className={formStyles.dataValueRight}>{formatDecimal(lWertFiktivTotal, 2)}</td>
                            </tr>
                        )}
                        {showOffenBest && (
                            <tr>
                                <td>Lagerwert - Gesamt - Offene Bestellungen:</td>
                                <td className={formStyles.dataValueRight}>{formatDecimal(lWertOffenBestTotal, 2)}</td>
                            </tr>
                        )}
                        {showOffenBest && (
                            <tr>
                                <td>Lagerwert - Gesamt - Offene Bestellungen (D):</td>
                                <td className={formStyles.dataValueRight}>{formatDecimal(lWertOffenBestDTotal, 2)}</td>
                            </tr>
                        )}
                        {showOffenLief && (
                            <tr>
                                <td>Lagerwert - Gesamt - Offene Lieferungen:</td>
                                <td className={formStyles.dataValueRight}>{formatDecimal(lWertOffenLiefTotal, 2)}</td>
                            </tr>
                        )}
                        {showOffenLief && (
                            <tr>
                                <td>Lagerwert - Gesamt - Offene Lieferungen (D):</td>
                                <td className={formStyles.dataValueRight}>{formatDecimal(lWertOffenLiefDTotal, 2)}</td>
                            </tr>
                        )}
                        <tr>
                            <td>Lagerwert - Gesamt - Verfügbar:</td>
                            <td className={formStyles.dataValueRight}>{formatDecimal(lWertVerfuegbarTotal, 2)}</td>
                        </tr>
                        <tr>
                            <td>Lagerwert - Gesamt - Reserviert:</td>
                            <td className={formStyles.dataValueRight}>{formatDecimal(lWertReserviertTotal, 2)}</td>
                        </tr>
                    </table>
                )}
                {distinctArtikelGruppenNrList.map((artikelGruppenNr2) => {
                    let countGroup = 0;
                    let lWertTotalGroup = 0;
                    let lWertFiktivTotalGroup = 0;
                    let lWertVerfuegbarGroup = 0;
                    let lWertOffenBestGroup = 0;
                    let lWertOffenBestDGroup = 0;
                    let lWertOffenLiefGroup = 0;
                    let lWertOffenLiefDGroup = 0;
                    let lWertReserviertGroup = 0;
                    (artikelDetailListByArtikelGruppeNr[artikelGruppenNr2] || []).forEach((artikelDetail) => {
                        countGroup += 1;

                        if (artikelDetail.defaultKalkulation) {
                            const { value: artikelEhEk1 } = artikelCalculator.convertPreisByEinheit(
                                artikelDetail.artikel || defaultArtikel,
                                artikelDetail.defaultKalkulation?.ek1 || 0,
                                artikelDetail.defaultKalkulation?.ek1Eh,
                                artikelDetail.artikel?.einheit,
                            );

                            const { value: ek1ProEh } = artikelCalculator.convertPreisByEinheit(
                                artikelDetail.artikel || defaultArtikel,
                                artikelEhEk1,
                                artikelDetail.artikel?.einheit,
                                artikelDetail.defaultKalkulation?.eh,
                            );

                            lWertTotalGroup += ek1ProEh * artikelDetail.bestand;
                            lWertFiktivTotalGroup += ek1ProEh * (artikelDetail.bestand + artikelDetail.offenBest);
                            lWertVerfuegbarGroup += ek1ProEh * (artikelDetail.bestand - artikelDetail.offenRes);
                            lWertOffenBestGroup += ek1ProEh * artikelDetail.offenBest;
                            lWertOffenBestDGroup += ek1ProEh * artikelDetail.offenBestD;
                            lWertOffenLiefGroup += ek1ProEh * artikelDetail.offenLief;
                            lWertOffenLiefDGroup += ek1ProEh * artikelDetail.offenLiefD;
                            lWertReserviertGroup += ek1ProEh * artikelDetail.offenRes;
                        }
                    });

                    return (
                        <React.Fragment key={artikelGruppenNr2}>
                            <h2>{artikelGruppenNr2 ? artikelGruppenByNr[artikelGruppenNr2]?.text || '?' : '?'}</h2>
                            {showAll && (
                                <table className={formStyles.reportSummaryTable}>
                                    <tr>
                                        <td>Artikel - Gruppe: </td>
                                        <td className={formStyles.dataValueRight}>{countGroup}</td>
                                    </tr>
                                    <tr>
                                        <td>Lagerwert - Gruppe - Bestand: </td>
                                        <td className={formStyles.dataValueRight}>
                                            {formatDecimal(lWertTotalGroup, 2)}
                                        </td>
                                    </tr>
                                    {showOffenBest && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Bestand + Offene Bestellungen:</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertFiktivTotalGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                    {showOffenBest && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Offene Bestellungen:</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertOffenBestGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                    {showOffenBest && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Offene Bestellungen (D):</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertOffenBestDGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                    {showOffenLief && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Offene Lieferungen:</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertOffenLiefGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                    {showOffenLief && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Offene Lieferungen (D):</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertOffenLiefDGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Lagerwert - Gruppe - Verfügbar:</td>
                                        <td className={formStyles.dataValueRight}>
                                            {formatDecimal(lWertVerfuegbarGroup, 2)}
                                        </td>
                                    </tr>
                                    {showAll && showLagerwert && (
                                        <tr>
                                            <td>Lagerwert - Gruppe - Reserviert:</td>
                                            <td className={formStyles.dataValueRight}>
                                                {formatDecimal(lWertReserviertGroup, 2)}
                                            </td>
                                        </tr>
                                    )}
                                </table>
                            )}
                            <table className={formStyles.reportTable}>
                                <thead>{renderHeaderRow()}</thead>
                                <tbody>
                                    {(artikelDetailListByArtikelGruppeNr[artikelGruppenNr2] || []).map(
                                        (artikelDetail) => {
                                            const { value: artikelEhEk1 } = artikelCalculator.convertPreisByEinheit(
                                                artikelDetail.artikel || defaultArtikel,
                                                artikelDetail.defaultKalkulation?.ek1 || 0,
                                                artikelDetail.defaultKalkulation?.ek1Eh,
                                                artikelDetail.artikel?.einheit,
                                            );

                                            const { value: ek1ProEh } = artikelCalculator.convertPreisByEinheit(
                                                artikelDetail.artikel || defaultArtikel,
                                                artikelEhEk1,
                                                artikelDetail.artikel?.einheit,
                                                artikelDetail.defaultKalkulation?.eh,
                                            );

                                            return (
                                                <tr
                                                    key={artikelDetail.artikelGruppeNr + '|' + artikelDetail.artikelNr}
                                                    className={styles.tableRow}
                                                >
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            printView
                                                                ? formStyles.reportLagerArtikelNrPrintCell
                                                                : formStyles.reportLagerArtikelNrCell,
                                                        )}
                                                    >
                                                        {artikelDetail.lagerArtikelNr}
                                                    </td>
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            printView
                                                                ? formStyles.reportLagerPrintCell
                                                                : formStyles.reportLagerCell,
                                                        )}
                                                    >
                                                        {artikelDetail.lagerIntNr
                                                            ? lagerByIntNr[artikelDetail.lagerIntNr]?.name ||
                                                              artikelDetail.lagerIntNr
                                                            : '-'}
                                                    </td>
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            printView
                                                                ? formStyles.reportArtikelTextPrintCell
                                                                : formStyles.reportArtikelTextCell,
                                                        )}
                                                    >
                                                        <strong>
                                                            {printView ? (
                                                                artikelDetail.artikelNr
                                                            ) : (
                                                                <OpenLink
                                                                    link={createRecordUrl(
                                                                        'lager/artikel',
                                                                        undefined,
                                                                        getArtikelDetailId(
                                                                            artikelDetail.artikelGruppeNr,
                                                                            artikelDetail.artikelNr,
                                                                        ),
                                                                        undefined,
                                                                        false,
                                                                    )}
                                                                    label={artikelDetail.artikelNr}
                                                                />
                                                            )}
                                                        </strong>
                                                        <br />
                                                        {artikelDetail.artikel?.text || '?'}
                                                    </td>
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            printView
                                                                ? formStyles.reportEinheitPrintCell
                                                                : formStyles.reportEinheitCell,
                                                        )}
                                                    >
                                                        {artikelDetail.artikel?.einheit
                                                            ? einheitenByCode[artikelDetail.artikel.einheit]?.text ||
                                                              artikelDetail.artikel.einheit
                                                            : '?'}
                                                    </td>
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            formStyles.dataValueRight,
                                                            printView
                                                                ? formStyles.reportMoneyPrintCell
                                                                : formStyles.reportMoneyCell,
                                                        )}
                                                    >
                                                        <strong>{formatDecimal(artikelDetail.bestand, 2)}</strong>
                                                    </td>
                                                    {showAll && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {formatDecimal(artikelDetail.minBestand, 2)}
                                                        </td>
                                                    )}
                                                    {showAll && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                            title={artikelDetail.drehungAnz + ' Aufträge'}
                                                        >
                                                            {formatDecimal(artikelDetail.drehung, 2)}
                                                        </td>
                                                    )}
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            formStyles.dataValueRight,
                                                            printView
                                                                ? formStyles.reportMoneyPrintCell
                                                                : formStyles.reportMoneyCell,
                                                        )}
                                                    >
                                                        <strong>
                                                            {formatDecimal(
                                                                artikelDetail.bestand - artikelDetail.offenRes,
                                                                2,
                                                            )}
                                                        </strong>
                                                    </td>
                                                    {showOffenBest && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {formatDecimal(artikelDetail.offenBest, 2)}
                                                        </td>
                                                    )}
                                                    {showOffenBest && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {formatDecimal(artikelDetail.offenBestD, 2)}
                                                        </td>
                                                    )}
                                                    {showOffenLief && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {formatDecimal(artikelDetail.offenLief, 2)}
                                                        </td>
                                                    )}
                                                    {showOffenLief && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {formatDecimal(artikelDetail.offenLiefD, 2)}
                                                        </td>
                                                    )}
                                                    <td
                                                        className={classnames(
                                                            styles.tdBody,
                                                            formStyles.dataValueRight,
                                                            printView
                                                                ? formStyles.reportMoneyPrintCell
                                                                : formStyles.reportMoneyCell,
                                                        )}
                                                    >
                                                        {formatDecimal(artikelDetail.offenRes, 2)}
                                                    </td>
                                                    {showAll && showLagerwert && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {
                                                                /*
                                                        artikelDetail.hauptlieferant ? formatDecimal(ek1ProEh, 3) : '?'
                                                        */
                                                                artikelDetail.defaultKalkulation
                                                                    ? formatDecimal(ek1ProEh, 3)
                                                                    : '?'
                                                            }
                                                        </td>
                                                    )}
                                                    {showAll && showLagerwert && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {
                                                                /*
                                                        artikelDetail.hauptlieferant
                                                        ? formatDecimal(artikelDetail.hauptlieferant.ekPreis, 3)
                                                        : '?'
                                                         */
                                                                artikelDetail.defaultKalkulation
                                                                    ? formatDecimal(
                                                                          artikelDetail.defaultKalkulation.ek2,
                                                                          3,
                                                                      )
                                                                    : '?'
                                                            }
                                                        </td>
                                                    )}
                                                    {showAll && showLagerwert && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {
                                                                /*
                                                        artikelDetail.hauptlieferant
                                                        ? formatDecimal(
                                                              artikelDetail.hauptlieferant.ekPreis *
                                                                  artikelDetail.bestand,
                                                              2,
                                                          )
                                                        : '?'
                                                         */
                                                                artikelDetail.defaultKalkulation
                                                                    ? formatDecimal(ek1ProEh * artikelDetail.bestand, 2)
                                                                    : '?'
                                                            }
                                                        </td>
                                                    )}
                                                    {showAll && showLagerwert && (
                                                        <td
                                                            className={classnames(
                                                                styles.tdBody,
                                                                formStyles.dataValueRight,
                                                                printView
                                                                    ? formStyles.reportMoneyPrintCell
                                                                    : formStyles.reportMoneyCell,
                                                            )}
                                                        >
                                                            {
                                                                /*
                                                        artikelDetail.hauptlieferant
                                                        ? formatDecimal(
                                                              artikelDetail.hauptlieferant.ekPreis *
                                                                  (artikelDetail.bestand + artikelDetail.offenBest),
                                                              2,
                                                          )
                                                        : '?'
                                                         */
                                                                artikelDetail.defaultKalkulation
                                                                    ? formatDecimal(
                                                                          ek1ProEh *
                                                                              (artikelDetail.bestand +
                                                                                  artikelDetail.offenBest),
                                                                          2,
                                                                      )
                                                                    : '?'
                                                            }
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        },
                                    )}
                                </tbody>
                            </table>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    },
);
